// Pure parser for the `subscribe.market_probabilities` feed, factored out of the
// provider so the subscription hook and the reducer/projection tests all share
// one definition. No React, no WS -- every export here is pure and unit-testable.
//
// The backend pushes async `update` messages: { method:'update', params:{
// channel_id, data } }. `data` carries the whole board for ONE token:
//
//   data = {
//     token, exchange, instrument_type, timestamp, pay_token, pay_time,
//     expiries: {
//       month_end: { "<expiry>": <node> },   // calendar month-end (prediction)
//       exchange:  { "<expiry>": <node> },   // Deribit-listed expiries
//     }
//   }
//   <node>  = { anchor_spot, atm_vol, rr_25, instruments }
//   instruments = { bs, lv, sv }, where each model is EITHER a flat [leaf] array
//     (legacy, one implicit settlement variant) OR nested one level deeper as
//     [pay_time][pay_token] = [leaf] -- the four settlement variants as peers
//     (at_touch|at_expiry x USD/USDT|<base-coin>). The nested shape carries NO
//     top-level pay_time/pay_token.
//   <leaf>  = { pv, error, level, direction }
//
// The parser normalises this into a per-family -> per-expiry -> per-level model,
// merging the bs/lv/sv leaf arrays (which share a level grid) into one row per
// level. sv is parsed but not yet rendered by the board (deferred follow-up);
// keeping it in the parser makes the later slice additive. Levels are returned
// sorted DESCENDING so the card reads top-of-ladder = highest level.
import { asRecord } from '../exotics-engine/engine-wire';
import { directionFor, type TouchDirection } from '../../pages/market-probabilities/ladder';

export const FEED_FAMILIES = ['month_end', 'exchange'] as const;
export type FeedFamily = (typeof FEED_FAMILIES)[number];

// One level row: the touch probability under each model at `level`, plus the
// error carried from the leaf (non-null when the backend couldn't price it).
// pvSv is parsed for the later sv column; the board renders bs/lv only for now.
export interface FeedLevel {
  level: number;
  direction: TouchDirection;
  pvBs: number | null;
  pvLv: number | null;
  pvSv: number | null;
  error: string | null;
}

// A settlement variant's coordinates exactly as they key the payload
// (instruments[model][pay_time][pay_token]). Raw strings, pre-semantic; the
// onTouch/inQuote the board selects on are derived just below. For the legacy
// flat shape (instruments[model] = [leaf]) both are '' -- one implicit variant.
export interface FeedVariantKey {
  payTime: string;
  payToken: string;
}

// Canonical map key for a (payTime, payToken) variant -- a unit-separator join so
// it can never collide with a symbol that happens to contain the delimiter.
export const variantId = (payTime: string, payToken: string): string =>
  `${payTime}::${payToken}`;

// One (family, expiry) node: the pricing anchor + surface summary carried by the
// feed (used directly -- no client derivation) plus the level rows PER settlement
// variant. The backend nests instruments[model][pay_time][pay_token] = [leaf]
// (four variants as peers); the legacy flat instruments[model] = [leaf] parses to
// a single variantId('', '').
export interface FeedNode {
  anchorSpot: number | null;
  atmVol: number | null;
  rr25: number | null;
  variants: Record<string, FeedLevel[]>;
  variantKeys: FeedVariantKey[];
}

// --- settlement-variant classification (registry-independent) ---
// pay_time 'at_touch' -> onTouch; 'at_expiry' -> at expiry; '' (legacy) -> touch.
export const payTimeOnTouch = (payTime: string): boolean =>
  payTime === '' || /touch/i.test(payTime);
// pay_token: a USD-family stable is quote-settled; the token's own coin symbol is
// base-settled. '' (legacy single variant) is treated as quote (the historical
// default). Registry-independent, so a not-yet-registered base token still
// classifies correctly.
const QUOTE_CCY = /^(usd|usdt|usdc|usdd|dai|busd|fdusd|tusd)$/i;
export const payTokenInQuote = (payToken: string): boolean =>
  payToken === '' || QUOTE_CCY.test(payToken);

// Pick the node's variantId matching the requested settlement. Exact (pay_time
// AND pay_token) first; else the first variant that at least agrees on pay_time;
// else the first variant present. Null only when the node carries no variants.
export function resolveVariantId(
  node: FeedNode,
  onTouch: boolean,
  inQuote: boolean,
): string | null {
  if (node.variantKeys.length === 0) return null;
  const exact = node.variantKeys.find(
    (k) => payTimeOnTouch(k.payTime) === onTouch && payTokenInQuote(k.payToken) === inQuote,
  );
  const byTime = node.variantKeys.find((k) => payTimeOnTouch(k.payTime) === onTouch);
  const chosen = exact ?? byTime ?? node.variantKeys[0];
  return variantId(chosen.payTime, chosen.payToken);
}

// The FeedLevel[] for a node's selected settlement variant (default at_touch +
// quote -- what the board historically showed). Pure; exported for consumers +
// tests that don't need the full RungQuote projection.
export function nodeLevels(node: FeedNode, onTouch = true, inQuote = true): FeedLevel[] {
  const id = resolveVariantId(node, onTouch, inQuote);
  return id ? (node.variants[id] ?? []) : [];
}

// The distinct settlement variants a set of nodes offers, resolved to (onTouch,
// inQuote) semantics -- drives the selector's enabled options. De-duped, ordered
// [at_touch first, quote first].
export function collectVariants(
  variantKeys: FeedVariantKey[],
): Array<{ onTouch: boolean; inQuote: boolean }> {
  const seen = new Set<string>();
  const out: Array<{ onTouch: boolean; inQuote: boolean }> = [];
  for (const k of variantKeys) {
    const onTouch = payTimeOnTouch(k.payTime);
    const inQuote = payTokenInQuote(k.payToken);
    const id = `${onTouch}|${inQuote}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ onTouch, inQuote });
  }
  return out.sort(
    (a, b) => Number(b.onTouch) - Number(a.onTouch) || Number(b.inQuote) - Number(a.inQuote),
  );
}

// The whole parsed board for ONE token.
export interface ParsedFeed {
  token: string;
  // The feed's as-of stamp (ISO). Drives the card's freshness/as-of readout.
  asOf: string | null;
  payToken: string | null;
  payTime: string | null;
  families: Record<FeedFamily, Record<string, FeedNode>>;
}

const toNum = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

// Parse the feed's as-of timestamp to an epoch (ms). The backend emits a NAIVE
// UTC ISO string with no zone designator (e.g. "2026-08-01T07:29:20.369438");
// JS Date.parse reads a zone-less date-TIME as LOCAL, which shifts the instant
// by the host offset (an hour early on BST) and inflates "time since update".
// Append 'Z' to force UTC -- but only when no zone is already present, so an
// already-zoned string ('...Z' or '...+01:00') is left correct. Returns null on
// a missing/unparseable stamp. Pure, timezone-independent -- unit tested.
export function parseUtc(ts: string | null | undefined): number | null {
  if (!ts) return null;
  const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(ts);
  const ms = Date.parse(hasZone ? ts : `${ts}Z`);
  return Number.isFinite(ms) ? ms : null;
}

const toStr = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

// Map a raw leaf direction onto the ladder's TouchDirection, tolerating both the
// 'upper'/'lower' and 'up'/'down' spellings the feed has used. Only consulted as
// a fallback when the anchor spot is unknown (otherwise direction is derived
// from level vs anchor, which is self-consistent with the HIT logic).
const rawDirection = (v: unknown): TouchDirection | null => {
  if (v === 'upper' || v === 'up') return 'upper';
  if (v === 'lower' || v === 'down') return 'lower';
  return null;
};

// Merge the bs/lv/sv leaf arrays that belong to ONE settlement variant into level
// rows. A level present in only some models keeps null for the missing legs
// (sparse tokens degrade gracefully). Direction is derived from level vs
// anchorSpot when known, else from the leaf's own direction field.
function mergeVariantLeaves(
  arrays: Array<{ field: 'pvBs' | 'pvLv' | 'pvSv'; arr: unknown }>,
  anchorSpot: number | null,
): FeedLevel[] {
  const byLevel = new Map<
    number,
    { pvBs: number | null; pvLv: number | null; pvSv: number | null; error: string | null; dir: TouchDirection | null }
  >();

  for (const { field, arr } of arrays) {
    if (!Array.isArray(arr)) continue;
    for (const leaf of arr) {
      const l = asRecord(leaf);
      if (!l) continue;
      const level = toNum(l.level);
      if (level === null || level <= 0) continue;
      const row = byLevel.get(level) ?? {
        pvBs: null,
        pvLv: null,
        pvSv: null,
        error: null,
        dir: null,
      };
      row[field] = toNum(l.pv);
      if (l.error != null && l.error !== '') row.error = String(l.error);
      if (row.dir === null) row.dir = rawDirection(l.direction);
      byLevel.set(level, row);
    }
  }

  return [...byLevel.entries()]
    .map(([level, row]) => ({
      level,
      direction:
        anchorSpot !== null ? directionFor(level, anchorSpot) : (row.dir ?? directionFor(level, level)),
      pvBs: row.pvBs,
      pvLv: row.pvLv,
      pvSv: row.pvSv,
      error: row.error,
    }))
    .sort((a, b) => b.level - a.level);
}

// Parse one (family, expiry) node. instruments[model] is EITHER the legacy flat
// shape ([leaf], one implicit settlement variant) OR the nested shape
// ([pay_time][pay_token] = [leaf], the four settlement variants as peers). We
// discover the shape per model and group the leaf arrays by variant so bs/lv/sv
// are merged WITHIN each variant. Unknown/missing instruments yield an empty
// variants map (the board shows an empty ladder, never a crash) -- which is
// exactly the regression this replaces: the old parser skipped a non-array
// instruments[model] outright, blanking the board when the nested shape landed.
function parseNode(raw: unknown): FeedNode {
  const r = asRecord(raw) ?? {};
  const anchorSpot = toNum(r.anchor_spot);
  const instruments = asRecord(r.instruments) ?? {};

  const models: Array<['pvBs' | 'pvLv' | 'pvSv', string]> = [
    ['pvBs', 'bs'],
    ['pvLv', 'lv'],
    ['pvSv', 'sv'],
  ];

  // variantId -> { key, leaf-arrays across models }, preserving first-seen order.
  const acc = new Map<
    string,
    { key: FeedVariantKey; arrays: Array<{ field: 'pvBs' | 'pvLv' | 'pvSv'; arr: unknown }> }
  >();
  const add = (
    payTime: string,
    payToken: string,
    field: 'pvBs' | 'pvLv' | 'pvSv',
    arr: unknown,
  ) => {
    const id = variantId(payTime, payToken);
    const entry = acc.get(id) ?? { key: { payTime, payToken }, arrays: [] };
    entry.arrays.push({ field, arr });
    acc.set(id, entry);
  };

  for (const [field, key] of models) {
    const v = instruments[key];
    if (Array.isArray(v)) {
      // Legacy flat shape: one implicit variant keyed ('', '').
      add('', '', field, v);
      continue;
    }
    const byPayTime = asRecord(v);
    if (!byPayTime) continue;
    for (const [payTime, ptVal] of Object.entries(byPayTime)) {
      const byPayToken = asRecord(ptVal);
      if (!byPayToken) continue;
      for (const [payToken, arr] of Object.entries(byPayToken)) add(payTime, payToken, field, arr);
    }
  }

  const variants: Record<string, FeedLevel[]> = {};
  const variantKeys: FeedVariantKey[] = [];
  for (const [id, entry] of acc) {
    variants[id] = mergeVariantLeaves(entry.arrays, anchorSpot);
    variantKeys.push(entry.key);
  }

  return { anchorSpot, atmVol: toNum(r.atm_vol), rr25: toNum(r.rr_25), variants, variantKeys };
}

function parseFamily(raw: unknown): Record<string, FeedNode> {
  const fam = asRecord(raw);
  if (!fam) return {};
  const out: Record<string, FeedNode> = {};
  for (const [expiry, node] of Object.entries(fam)) out[expiry] = parseNode(node);
  return out;
}

// Parse a raw feed message into ParsedFeed. Accepts either the full `update`
// envelope ({ params: { data } }) or a bare `data` payload, so the hook can pass
// the message straight through and the tests can pass a data object. Returns null
// when the payload isn't a market-probabilities board (no token / no expiries) so
// the caller can drop it without touching state.
export function parseMarketProbabilitiesFeed(raw: unknown): ParsedFeed | null {
  const top = asRecord(raw);
  if (!top) return null;
  // Unwrap the update envelope if present.
  const params = asRecord(top.params);
  const data = params ? (asRecord(params.data) ?? asRecord(top)) : asRecord(top);
  if (!data) return null;

  const token = toStr(data.token);
  const expiries = asRecord(data.expiries);
  if (token === null || !expiries) return null;

  return {
    token,
    asOf: toStr(data.timestamp),
    payToken: toStr(data.pay_token),
    payTime: toStr(data.pay_time),
    families: {
      month_end: parseFamily(expiries.month_end),
      exchange: parseFamily(expiries.exchange),
    },
  };
}
